import type { RouteObject } from 'react-router-dom';
import React, { lazy } from 'react';

// Lazy Loading für alle Seiten
const HomePage = lazy(() => import('../pages/HomePage'));
const ImpressumPage = lazy(() => import('../pages/ImpressumPage'));
const PrivacyPage = lazy(() => import('../pages/PrivacyPage'));
const AGBPage = lazy(() => import('../pages/AGBPage'));
const AffiliatePage = lazy(() => import('../pages/AffiliatePage'));
const BeratungsterminPage = lazy(() => import('../pages/BeratungsterminPage'));
const ServiceDetailPage = lazy(() => import('../pages/ServiceDetailPage'));

// Fehlerseiten
const NotFoundPage = lazy(() => import('../pages/NotFoundPage'));
const ServerErrorPage = lazy(() => import('../pages/ServerErrorPage'));
const ForbiddenPage = lazy(() => import('../pages/ForbiddenPage'));
const UnauthorizedPage = lazy(() => import('../pages/UnauthorizedPage'));
const NetworkErrorPage = lazy(() => import('../pages/NetworkErrorPage'));
const MaintenancePage = lazy(() => import('../pages/MaintenancePage'));
const ServiceUnavailablePage = lazy(() => import('../pages/ServiceUnavailablePage'));
const BadRequestPage = lazy(() => import('../pages/BadRequestPage'));

// Route-Typen für bessere Typisierung
export interface RouteConfig {
  path: string;
  element: React.ComponentType;
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  breadcrumbs?: Array<{ label: string; path?: string }>;
  preload?: boolean; // Für wichtige Routen
  index?: boolean;
}

// Zentrale Route-Konfiguration
export const routeConfigs: RouteConfig[] = [
  {
    path: '/',
    element: HomePage,
    title: 'StimmDigital - App- & Webentwicklung aus Berlin',
    description: 'Professionelle App- und Webentwicklung aus Berlin. Maßgeschneiderte digitale Lösungen für dein Unternehmen. React, Node.js, Mobile Apps und mehr.',
    keywords: 'App-Entwicklung, Webentwicklung, React, Node.js, Berlin, StimmDigital, Mobile Apps, Progressive Web Apps',
    canonical: '/',
    preload: true,
    index: true
  },
  {
    path: '/beratungstermin',
    element: BeratungsterminPage,
    title: 'Kostenloses Beratungsgespräch | StimmDigital',
    description: 'Vereinbare ein kostenloses Beratungsgespräch für dein App- oder Webprojekt. Professionelle Beratung und unverbindliche Projektanalyse.',
    keywords: 'Beratungstermin, Kostenlos, App-Beratung, Web-Beratung, Projektanalyse',
    canonical: '/beratungstermin',
    breadcrumbs: [
      { label: 'Home', path: '/' },
      { label: 'Beratungstermin' }
    ]
  },
  {
    path: '/:serviceId',
    element: ServiceDetailPage,
    title: 'Unsere Services | StimmDigital',
    description: 'Professionelle Softwareentwicklung Services.',
    breadcrumbs: [
      { label: 'Home', path: '/' },
      { label: 'Services' }
    ]
  },
  {
    path: '/affiliate',
    element: AffiliatePage,
    title: 'Affiliate-Programm | StimmDigital - Partner werden',
    description: 'Werde Partner von StimmDigital und profitiere von unserem Affiliate-Programm. Attraktive Provisionen für erfolgreiche Vermittlungen.',
    keywords: 'Affiliate, Partner, Provisionen, Partnerprogramm, Empfehlungen',
    canonical: '/affiliate',
    breadcrumbs: [
      { label: 'Home', path: '/' },
      { label: 'Affiliate-Programm' }
    ]
  },
  {
    path: '/impressum',
    element: ImpressumPage,
    title: 'Impressum | StimmDigital',
    description: 'Impressum und Kontaktdaten von StimmDigital - App- und Webentwicklung aus Berlin.',
    canonical: '/impressum',
    breadcrumbs: [
      { label: 'Home', path: '/' },
      { label: 'Impressum' }
    ]
  },
  {
    path: '/datenschutz',
    element: PrivacyPage,
    title: 'Datenschutzerklärung | StimmDigital',
    description: 'Datenschutzerklärung von StimmDigital. Informationen zum Umgang mit personenbezogenen Daten gemäß DSGVO.',
    canonical: '/datenschutz',
    breadcrumbs: [
      { label: 'Home', path: '/' },
      { label: 'Datenschutzerklärung' }
    ]
  },
  {
    path: '/agb',
    element: AGBPage,
    title: 'Allgemeine Geschäftsbedingungen | StimmDigital',
    description: 'Allgemeine Geschäftsbedingungen von StimmDigital für App- und Webentwicklungsdienstleistungen.',
    canonical: '/agb',
    breadcrumbs: [
      { label: 'Home', path: '/' },
      { label: 'AGB' }
    ]
  }
];

// Error-Routen für direkte Navigation
export const errorRouteConfigs: RouteConfig[] = [
  {
    path: '/error/400',
    element: BadRequestPage,
    title: 'Ungültige Anfrage | StimmDigital',
    description: 'Die Anfrage konnte nicht verarbeitet werden.',
    canonical: '/error/400'
  },
  {
    path: '/error/401',
    element: UnauthorizedPage,
    title: 'Nicht autorisiert | StimmDigital',
    description: 'Du hast keine Berechtigung für diese Seite.',
    canonical: '/error/401'
  },
  {
    path: '/error/403',
    element: ForbiddenPage,
    title: 'Zugriff verweigert | StimmDigital',
    description: 'Der Zugriff auf diese Ressource wurde verweigert.',
    canonical: '/error/403'
  },
  {
    path: '/error/404',
    element: NotFoundPage,
    title: 'Seite nicht gefunden | StimmDigital',
    description: 'Die angeforderte Seite konnte nicht gefunden werden.',
    canonical: '/error/404'
  },
  {
    path: '/error/500',
    element: ServerErrorPage,
    title: 'Server-Fehler | StimmDigital',
    description: 'Es ist ein Server-Fehler aufgetreten.',
    canonical: '/error/500'
  },
  {
    path: '/error/503',
    element: ServiceUnavailablePage,
    title: 'Service nicht verfügbar | StimmDigital',
    description: 'Der Service ist vorübergehend nicht verfügbar.',
    canonical: '/error/503'
  },
  {
    path: '/maintenance',
    element: MaintenancePage,
    title: 'Wartungsarbeiten | StimmDigital',
    description: 'Wir führen derzeit Wartungsarbeiten durch.',
    canonical: '/maintenance'
  },
  {
    path: '/network-error',
    element: NetworkErrorPage,
    title: 'Verbindungsfehler | StimmDigital',
    description: 'Es konnte keine Verbindung hergestellt werden.',
    canonical: '/network-error'
  }
];

// Legacy-Redirects für SEO
export const redirects: Record<string, string> = {
  '/privacy': '/datenschutz',
  '/imprint': '/impressum',
  '/terms': '/agb',
  '/consultation': '/beratungstermin',
  '/de': '/'
};

// React Router Konfiguration
export const createRoutes = (): RouteObject[] => {
  const routes: RouteObject[] = routeConfigs.map(config => ({
    path: config.path,
    element: React.createElement(config.element),
    index: config.index
  }));

  // Error-Routen hinzufügen
  const errorRoutes: RouteObject[] = errorRouteConfigs.map(config => ({
    path: config.path,
    element: React.createElement(config.element)
  }));

  routes.push(...errorRoutes);

  // 404 Route
  routes.push({
    path: '*',
    element: React.createElement(NotFoundPage)
  });

  return routes;
};

// Hilfsfunktionen für Navigation
export const getRouteConfig = (pathname: string): RouteConfig | undefined => {
  return [...routeConfigs, ...errorRouteConfigs].find(route => route.path === pathname);
};

export const isValidRoute = (pathname: string): boolean => {
  return [...routeConfigs, ...errorRouteConfigs].some(route => route.path === pathname) || pathname === '*';
};

export const getCanonicalUrl = (pathname: string): string => {
  const config = getRouteConfig(pathname);
  return config?.canonical || pathname;
};

// Sitemap-Generation
export const generateSitemap = (): string[] => {
  return routeConfigs
    .filter(route => route.path !== '*')
    .map(route => route.canonical || route.path);
};
